import { Button, Dropdown, Input, Option } from '@sam/components';
import { Dispatch, SetStateAction, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { AbsenceReason, AppointmentState, Language, Schedule, useData } from 'shared';
import { generateDropdownOptionsForAbsenceReasons } from '../../utils/absenceReason/AbsenceReason.utils';
import { useUser } from '../UserContext';

interface ScheduleEditProps {
  schedule: Schedule;
  updateSchedule: Dispatch<SetStateAction<Schedule | undefined>>;
  handleScheduleCreate: (schedule: Schedule) => void;
  handleScheduleDelete: (id: string) => void;
}

export const ScheduleEdit: React.FC<ScheduleEditProps> = ({
  schedule,
  updateSchedule,
  handleScheduleCreate,
  handleScheduleDelete,
}) => {
  const { t } = useTranslation();
  const { user } = useUser();

  const { data: allAbsenceReasons } = useData('ABSENCE_REASONS_USER', {
    config: { fallbackData: [] },
    params: { userId: user.id },
  });

  /**
   * The selected absenceReason for the schedule
   */
  const selectedReason: AbsenceReason | undefined = useMemo((): AbsenceReason | undefined => {
    return allAbsenceReasons.find((reason) => reason.id === schedule.appointmentDetail?.reasonId);
  }, [schedule.appointmentDetail?.reasonId]);
  /**
   * Options to choose for a new entry
   */
  const reasons: Option[] = useMemo((): Option[] => {
    const selectableReasons: AbsenceReason[] = allAbsenceReasons.filter(
      (reason) => !reason.holidayReason && !reason.azkReduction,
    );
    return generateDropdownOptionsForAbsenceReasons(selectableReasons, user.language);
  }, [allAbsenceReasons, user.language]);

  return (
    <>
      <div className="three-columns">
        <Input
          label={t('pages.user.create.schedule.title')}
          value={schedule.appointmentDetail?.title}
          onChange={(title) =>
            updateSchedule((old) => ({
              ...old!,
              appointmentDetail: { ...old!.appointmentDetail!, title },
            }))
          }
          type="text"
        />
        <>
          <Input
            label={t('pages.user.create.schedule.startDate')}
            type="date"
            onChangeDate={(startDate) => updateSchedule({ ...schedule, startDate })}
            value={schedule.startDate}
          />

          <Input
            label={t('pages.user.create.schedule.startTime')}
            value={schedule.appointmentDetail?.startTime}
            onChangeTime={(startTime) =>
              updateSchedule((old) => ({
                ...old!,
                appointmentDetail: {
                  ...old!.appointmentDetail!,
                  startTime,
                },
              }))
            }
            type="time"
          />
          {selectedReason?.azkReduction || selectedReason?.holidayReason ? (
            <Input
              disabled
              type="text"
              value={selectedReason.translations.get(user.language || Language.DE)}
              label={t('pages.user.create.schedule.reasonId')}
            />
          ) : (
            <Dropdown
              label={t('pages.user.create.schedule.reasonId')}
              options={reasons}
              selectedOption={schedule.appointmentDetail?.reasonId}
              onChange={(reasonId) =>
                updateSchedule((old) => ({
                  ...old!,
                  appointmentDetail: {
                    ...old!.appointmentDetail!,
                    reasonId,
                  },
                }))
              }
            />
          )}
          <Input
            label={t('pages.user.create.schedule.endDate')}
            type="date"
            onChangeDate={(endDate) => updateSchedule({ ...schedule, endDate })}
            value={schedule.endDate}
          />
        </>

        <Input
          label={t('pages.user.create.schedule.endTime')}
          value={schedule.appointmentDetail?.endTime}
          onChangeTime={(endTime) =>
            updateSchedule((old) => ({
              ...old!,
              appointmentDetail: {
                ...old!.appointmentDetail!,
                endTime,
              },
            }))
          }
          type="time"
        />

        <Dropdown
          options={Object.keys(AppointmentState).map((state) => ({
            label: t(`pages.user.create.schedule.states.${state}`),
            value: state,
          }))}
          selectedOption={schedule.appointmentDetail?.state}
          onChange={(state: AppointmentState) =>
            updateSchedule((old) => ({
              ...old!,
              appointmentDetail: {
                ...old!.appointmentDetail!,
                state,
              },
            }))
          }
          label={t('pages.user.create.schedule.state')}
        />

        <div className="schedule-button-wrapper">
          <Button
            value={t(`pages.user.create.schedule.${schedule.id ? 'editButton' : 'createButton'}`)}
            onClick={() => {
              handleScheduleCreate(schedule);
            }}
            type="button"
          />
          {schedule.id && (
            <Button
              value={t(`pages.user.create.schedule.deleteButton`)}
              onClick={() => handleScheduleDelete(schedule.id)}
              type="button"
              backgroundColor="#bc2e46"
            />
          )}
        </div>
      </div>
    </>
  );
};
